import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '@core/api';
import config from '@core/config';

/**
 * Static mapping of Teamtailor job IDs to their organization.
 * Mirrors the backend's JOB_ID_TO_ORGANIZATION.
 */
export const JOB_ID_TO_ORGANIZATION = {
  '5100938': 'FASI',
  '1503775': 'RESILIS',
  '5257024': 'RESILIS',
  '5229987': 'RESILIS',
  '5230193': 'RESILIS',
};

/**
 * Returns all job_ids belonging to a given organization.
 */
export const getJobIdsByOrganization = (organization) =>
  Object.entries(JOB_ID_TO_ORGANIZATION)
    .filter(([, org]) => org === organization)
    .map(([id]) => id);

/**
 * Substitutes API
 *
 * RTK Query API for managing substitute candidates.
 * Provides access to substitute/candidate listing for emergency substitution workflow.
 *
 * Simplified to focus on substitute management only.
 * Emergency request and candidate response operations moved to emergencyApi.js
 *
 * Follows 2025 RTK Query best practices:
 * - Single responsibility (substitute candidates only)
 * - Automatic caching and request deduplication
 */
export const substitutesApi = createApi({
  reducerPath: 'substitutesApi',
  baseQuery: (args, api, extraOptions) => {
    // Prepend /api/v1 to all requests
    const adjustedArgs = typeof args === 'string'
      ? `/api/v1${args}`
      : { ...args, url: `/api/v1${args.url}` };
    return baseQuery(adjustedArgs, api, extraOptions);
  },
  tagTypes: ['Substitutes', 'Pools'],
  // Cache configuration
  keepUnusedDataFor: 300, // 5 minutes cache
  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({
    /**
     * Get all substitute candidates for the account.
     * @param {Object} [params]
     * @param {string} [params.organization] - Filter by organization (FASI, RESILIS, etc.)
     * @param {string[]} [params.jobIds] - Filter by specific Teamtailor job IDs (takes precedence over organization)
     * @returns Array of substitute candidate objects
     */
    getSubstitutes: builder.query({
      query: (params) => {
        // Legacy: accept plain string for backwards compat
        if (typeof params === 'string') {
          return params ? `/job-substitutes/?organization=${params}` : '/job-substitutes/';
        }
        const { organization, jobIds } = params || {};
        if (jobIds && jobIds.length > 0) {
          return `/job-substitutes/?job_ids=${jobIds.join(',')}`;
        }
        if (organization) {
          return `/job-substitutes/?organization=${organization}`;
        }
        return '/job-substitutes/';
      },
      providesTags: ['Substitutes'],
    }),

    /**
     * Get available candidate pools (bolsas) for the account.
     * @returns Array of pool objects: { job_id, organization }
     * Use POOL_NAMES[job_id] to get the human-readable name.
     */
    getAvailablePools: builder.query({
      query: () => '/job-substitutes/pools/',
      providesTags: ['Pools'],
    }),
  }),
});

export const {
  useGetSubstitutesQuery,
  useLazyGetSubstitutesQuery,
  useGetAvailablePoolsQuery,
} = substitutesApi;
